<template>
  <BaseButtonDropdown
    v-model="dropdownOpen"
    class="tw:h-fit tw:pb-1"
    new-dropdown
    placement="bottom-start"
  >
    <template #btn="{ toggle }">
      <slot
        name="btn"
        :toggle="toggle"
      >
        <div>
          <BaseButton
            circle
            class="selector-button"
            color="primary"
            icon="thunderbolt"
            new-design
            new-icon
            size="sm"
            @click="toggle"
          />
          <StreemTooltip
            v-if="isDesktop"
            anchor="top middle"
            self="bottom middle"
            >{{
              getLocaleText(
                "stream_settings_simple_boolean_selector.search_tooltip"
              )
            }}</StreemTooltip
          >
        </div>
      </slot>
    </template>
    <div
      v-if="showKeyword"
      class="tw:flex tw:flex-col"
    >
      <BaseButton
        :active="!activeOption"
        :label="
          getLocaleText('stream_settings_simple_boolean_selector.keyword_label')
        "
        menu-item
        new-design
        square
        @click="applyKeywordFilter"
      />
    </div>
    <div class="tw:flex tw:flex-col">
      <BaseButton
        :active="activeOption === SimpleBooleanFilterType.CASE_SENSITIVE"
        :label="
          getLocaleText(
            'stream_settings_simple_boolean_selector.case_sensitive_label'
          )
        "
        menu-item
        new-design
        square
        @click="applyCaseSensitiveFilter"
      />
      <StreemTooltip
        v-if="isDesktop"
        anchor="center left"
        self="center right"
      >
        <div class="tw:flex tw:w-48 tw:flex-col tw:gap-2">
          <div>
            {{
              getLocaleText(
                "stream_settings_simple_boolean_selector.case_sensitive_tooltip_1"
              )
            }}
          </div>
          <div>
            {{
              getLocaleText(
                "stream_settings_simple_boolean_selector.case_sensitive_tooltip_2"
              )
            }}
          </div>
        </div>
      </StreemTooltip>
    </div>
    <div class="tw:flex tw:flex-col">
      <BaseButton
        :active="activeOption === SimpleBooleanFilterType.TITLE"
        :label="getLocaleText('global.title')"
        menu-item
        new-design
        @click="applyTitleFilter"
      />
      <StreemTooltip
        v-if="isDesktop"
        anchor="center left"
        self="center right"
      >
        <div class="tw:flex tw:w-48 tw:flex-col tw:gap-2">
          <div>
            {{
              getLocaleText(
                "stream_settings_simple_boolean_selector.title_tooltip_1"
              )
            }}
          </div>
          <div>
            {{
              getLocaleText(
                "stream_settings_simple_boolean_selector.title_tooltip_2"
              )
            }}
          </div>
        </div>
      </StreemTooltip>
    </div>
    <div class="tw:flex tw:flex-col">
      <BaseButton
        v-if="isDesktop"
        :active="activeOption === SimpleBooleanFilterType.AUTHOR"
        :label="
          getLocaleText('stream_settings_simple_boolean_selector.author_label')
        "
        menu-item
        new-design
        @click="applyAuthorFilter"
      />
      <StreemTooltip
        v-if="isDesktop"
        anchor="center left"
        self="center right"
      >
        <div class="tw:flex tw:w-48 tw:flex-col tw:gap-2">
          <div>
            {{
              getLocaleText(
                "stream_settings_simple_boolean_selector.author_tooltip_1"
              )
            }}
          </div>
          <div>
            {{
              getLocaleText(
                "stream_settings_simple_boolean_selector.author_tooltip_2"
              )
            }}
          </div>
          <div v-if="showOverrideTooltips">
            {{
              getLocaleText(
                "stream_settings_simple_boolean_selector.author_tooltip_override"
              )
            }}
          </div>
        </div>
      </StreemTooltip>
    </div>
    <div class="tw:flex tw:flex-col">
      <BaseButtonDropdown
        :active="activeOption === SimpleBooleanFilterType.MEDIUM"
        icon="chevron-right"
        icon-right
        :label="typeLabel"
        menu-item
        new-design
        new-dropdown
        new-icon
        :placement="dropdownPlacement"
        @click="clickOption(SimpleBooleanFilterType.MEDIUM)"
      >
        <div class="tw:flex tw:w-96 tw:flex-col tw:gap-4 tw:p-4">
          <div v-if="isSocialStream">
            {{
              getLocaleText(
                "stream_settings_simple_boolean_selector.social_platform_description"
              )
            }}
          </div>
          <div v-else>
            {{
              getLocaleText(
                "stream_settings_simple_boolean_selector.media_type_description"
              )
            }}
          </div>
          <div>
            <SocialStreamMediaTypeSelector
              v-if="isSocialStream"
              v-model="selectedSocialPlatforms"
              :columns="2"
              use-platform
            />
            <MediumSelector
              v-else
              v-model="media"
              class="tw:gap-y-2"
              :columns-per-row="2"
              dense
              new-design
            />
          </div>
          <div class="tw:flex tw:justify-end tw:gap-2">
            <BaseButton
              :label="getLocaleText('global.cancel')"
              new-design
              @click="cancel"
            />
            <BaseButton
              color="primary"
              :disabled="!canApplyMediumOrTypeFilter"
              :label="getLocaleText('global.apply')"
              new-design
              @click="applyMediumOrTypeFilter()"
            />
          </div>
        </div>

        <template #tooltip>
          <StreemTooltip
            v-if="isDesktop"
            anchor="center left"
            self="center right"
          >
            <div class="tw:flex tw:w-48 tw:flex-col tw:gap-2">
              <div>
                {{
                  getLocaleText(
                    "stream_settings_simple_boolean_selector.media_type_tooltip_1"
                  )
                }}
              </div>
              <div>
                {{
                  getLocaleText(
                    "stream_settings_simple_boolean_selector.media_type_tooltip_2"
                  )
                }}
              </div>
              <div v-if="showOverrideTooltips">
                {{
                  getLocaleText(
                    "stream_settings_simple_boolean_selector.media_type_tooltip_override"
                  )
                }}
              </div>
            </div>
          </StreemTooltip>
        </template>
      </BaseButtonDropdown>
    </div>
    <div class="tw:flex tw:flex-col">
      <BaseButtonDropdown
        :active="activeOption === SimpleBooleanFilterType.AT_LEAST"
        icon="chevron-right"
        icon-right
        :label="
          getLocaleText(
            'stream_settings_simple_boolean_selector.at_least_label'
          )
        "
        menu-item
        new-design
        new-dropdown
        new-icon
        :placement="dropdownPlacement"
        @click="clickOption(SimpleBooleanFilterType.AT_LEAST)"
      >
        <div
          class="tw:flex tw:w-80 tw:flex-col tw:gap-4 tw:p-4 tw:md:w-[380px]"
        >
          <i18n-t
            class="tw:flex tw:items-center tw:gap-2"
            keypath="stream_settings_simple_boolean_selector.at_least_description"
            scope="global"
            tag="div"
          >
            <template #mentioned>
              <div>
                {{
                  getLocaleText(
                    "stream_settings_simple_boolean_selector.at_least_description_mentioned"
                  )
                }}
              </div>
            </template>
            <template #limit>
              <div class="tw:flex tw:items-center tw:gap-2">
                <div class="tw:w-12">
                  <InputText
                    v-model="atLeastLimit"
                    new-design
                    placeholder="0"
                    :validations="['isNumber']"
                    @keydown.enter.prevent="applyAtLeastFilter"
                  />
                </div>
              </div>
            </template>
            <template #times>
              <div>
                {{
                  getLocaleText(
                    "stream_settings_simple_boolean_selector.at_least_description_times"
                  )
                }}
              </div>
            </template>
          </i18n-t>
          <div class="tw:flex tw:justify-end tw:gap-2">
            <BaseButton
              :label="getLocaleText('global.cancel')"
              new-design
              @click="cancel"
            />
            <BaseButton
              color="primary"
              :disabled="Number(atLeastLimit) < 1"
              :label="getLocaleText('global.apply')"
              new-design
              @click="applyAtLeastFilter"
            />
          </div>
        </div>

        <template #tooltip>
          <StreemTooltip
            v-if="isDesktop"
            anchor="center left"
            self="center right"
          >
            <div class="tw:flex tw:w-48 tw:flex-col tw:gap-2">
              <div>
                {{
                  getLocaleText(
                    "stream_settings_simple_boolean_selector.at_least_tooltip_1"
                  )
                }}
              </div>
              <div>
                {{
                  getLocaleText(
                    "stream_settings_simple_boolean_selector.at_least_tooltip_2"
                  )
                }}
              </div>
            </div>
          </StreemTooltip>
        </template>
      </BaseButtonDropdown>
    </div>
    <div class="tw:flex tw:flex-col">
      <BaseButtonDropdown
        :active="activeOption === SimpleBooleanFilterType.FIRST"
        class="tw:flex-1"
        icon="chevron-right"
        icon-right
        :label="
          getLocaleText('stream_settings_simple_boolean_selector.first_label')
        "
        menu-item
        new-design
        new-dropdown
        new-icon
        :placement="dropdownPlacement"
        @click="clickOption(SimpleBooleanFilterType.FIRST)"
      >
        <div
          class="tw:flex tw:w-80 tw:flex-col tw:gap-4 tw:p-4 tw:md:w-[460px]"
        >
          <i18n-t
            class="tw:flex tw:items-center tw:gap-2"
            keypath="stream_settings_simple_boolean_selector.first_description"
            scope="global"
            tag="div"
          >
            <template #mentioned>
              <div>
                {{
                  getLocaleText(
                    "stream_settings_simple_boolean_selector.first_description_mentioned"
                  )
                }}
              </div>
            </template>
            <template #limit>
              <div class="tw:flex tw:items-center tw:gap-2">
                <div class="tw:w-12">
                  <InputText
                    v-model="firstLimit"
                    new-design
                    placeholder="0"
                    :validations="['isNumber']"
                    @keydown.enter.prevent="applyFirstFilter"
                  />
                </div>
              </div>
            </template>
            <template #words>
              <div>
                {{
                  getLocaleText(
                    "stream_settings_simple_boolean_selector.first_description_words"
                  )
                }}
              </div>
            </template>
          </i18n-t>

          <div class="tw:flex tw:justify-end tw:gap-2">
            <BaseButton
              :label="getLocaleText('global.cancel')"
              new-design
              @click="cancel"
            />
            <BaseButton
              color="primary"
              :disabled="Number(firstLimit) < 1"
              :label="getLocaleText('global.apply')"
              new-design
              @click="applyFirstFilter"
            />
          </div>
        </div>

        <template #tooltip>
          <StreemTooltip
            v-if="isDesktop"
            anchor="center left"
            self="center right"
          >
            <div class="tw:flex tw:w-48 tw:flex-col tw:gap-2">
              <div>
                {{
                  getLocaleText(
                    "stream_settings_simple_boolean_selector.first_tooltip_1"
                  )
                }}
              </div>
              <div>
                {{
                  getLocaleText(
                    "stream_settings_simple_boolean_selector.first_tooltip_2"
                  )
                }}
              </div>
            </div>
          </StreemTooltip>
        </template>
      </BaseButtonDropdown>
    </div>
  </BaseButtonDropdown>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseButtonDropdown,
  InputText,
} from "shared/components/base";
import { SimpleBooleanOverrideTooltipSymbol } from "shared/components/base/symbols";
import type { Model as MediumSelectorModel } from "shared/components/selectors/MediumSelector.vue";
import MediumSelector from "shared/components/selectors/MediumSelector.vue";
import SocialStreamMediaTypeSelector from "shared/components/SocialStreamMediaTypeSelector.vue";
import {
  type MediumName,
  getSourceByField,
  sourceByMedium,
} from "shared/helpers/media";
import type { SimpleBooleanFilter } from "shared/resources/Stream";
import { SimpleBooleanFilterType } from "shared/resources/Stream";
import type { Nullable } from "shared/types";

const showOverrideTooltips = inject<boolean>(
  SimpleBooleanOverrideTooltipSymbol,
  false
);

const filter = defineModel<SimpleBooleanFilter>({ required: true });

interface SuggestedKeywordsBooleanSelectorProps {
  showKeyword?: boolean;
  isSocialStream?: boolean;
}

const props = defineProps<SuggestedKeywordsBooleanSelectorProps>();

type Events = {
  filter: [SimpleBooleanFilter];
  cancel: [];
};

const emit = defineEmits<Events>();

const originalFilter = ref<SimpleBooleanFilter>({});
const isDesktop = inject<boolean>("isDesktop");
const dropdownOpen = ref(false);
const activeOption = ref<Nullable<SimpleBooleanFilterType>>(null);
const firstLimit = ref<Nullable<string>>(null);
const atLeastLimit = ref<Nullable<string>>(null);

const media = ref<MediumSelectorModel>({
  online_content: false,
  print_content: false,
  magazine_content: false,
  tv_content: false,
  radio_content: false,
  social_content: false,
  podcast_content: false,
});

const selectedSocialPlatforms = ref<string[]>([]);

const dropdownPlacement = computed(() =>
  isDesktop ? "right" : "bottom-start"
);

const selectedMedia = computed<MediumName[]>(() =>
  Object.entries(media.value).reduce((accumulator, [key, checked]) => {
    const { medium } = getSourceByField(key.replace("_content", ""));

    if (checked) {
      accumulator.push(medium);
    }

    return accumulator;
  }, [] as MediumName[])
);

const canApplyMediumOrTypeFilter = computed(() =>
  Boolean(selectedMedia.value.length || selectedSocialPlatforms.value.length)
);

const typeLabel = computed(() => {
  if (props.isSocialStream) {
    return getLocaleText(
      "stream_settings_simple_boolean_selector.social_platform_label"
    );
  }

  return getLocaleText(
    "stream_settings_simple_boolean_selector.media_type_label"
  );
});

function closeDropdown() {
  dropdownOpen.value = false;
}

function clickOption(type: SimpleBooleanFilterType) {
  activeOption.value = type;
}

function applyKeywordFilter() {
  filter.value = {
    keyword: filter.value.keyword,
  };

  closeDropdown();
  activeOption.value = null;
}

function applyTitleFilter() {
  filter.value = {
    type: SimpleBooleanFilterType.TITLE,
    keyword: filter.value.keyword,
  };

  closeDropdown();
  activeOption.value = SimpleBooleanFilterType.TITLE;
}

function applyCaseSensitiveFilter() {
  filter.value = {
    type: SimpleBooleanFilterType.CASE_SENSITIVE,
    keyword: filter.value.keyword,
  };

  closeDropdown();
  activeOption.value = SimpleBooleanFilterType.TITLE;
}

function applyAuthorFilter() {
  filter.value = {
    type: SimpleBooleanFilterType.AUTHOR,
    keyword: filter.value.keyword,
  };

  closeDropdown();
  activeOption.value = SimpleBooleanFilterType.TITLE;
}

function applyAtLeastFilter() {
  if (Number(atLeastLimit.value) > 0) {
    filter.value = {
      type: SimpleBooleanFilterType.AT_LEAST,
      value: Number(atLeastLimit.value),
      keyword: filter.value.keyword,
    };

    closeDropdown();
  }
}

function applyFirstFilter() {
  if (Number(firstLimit.value) > 0) {
    filter.value = {
      type: SimpleBooleanFilterType.FIRST,
      value: Number(firstLimit.value),
      keyword: filter.value.keyword,
    };

    closeDropdown();
  }
}

function applyMediumOrTypeFilter() {
  if (!canApplyMediumOrTypeFilter.value) return;

  if (props.isSocialStream) {
    filter.value = {
      type: SimpleBooleanFilterType.PLATFORM,
      value: selectedSocialPlatforms.value,
      keyword: filter.value.keyword,
    };

    closeDropdown();

    return;
  }

  filter.value = {
    type: SimpleBooleanFilterType.MEDIUM,
    value: selectedMedia.value,
    keyword: filter.value.keyword,
  };

  closeDropdown();
}

function setMediaTypes() {
  media.value = {
    online_content: false,
    print_content: false,
    magazine_content: false,
    tv_content: false,
    radio_content: false,
    social_content: false,
    podcast_content: false,
  };

  if (
    filter.value.type === SimpleBooleanFilterType.MEDIUM &&
    Array.isArray(filter.value.value)
  ) {
    filter.value.value.forEach((medium) => {
      const source = sourceByMedium[medium];
      media.value[`${source.field}_content`] = true;
    });
  }
}

function setSocialPlatforms() {
  selectedSocialPlatforms.value = [];

  if (
    filter.value.type === SimpleBooleanFilterType.PLATFORM &&
    Array.isArray(filter.value.value)
  ) {
    selectedSocialPlatforms.value = filter.value.value;
  }
}

function reset() {
  firstLimit.value =
    filter.value.type === SimpleBooleanFilterType.FIRST &&
    Number(filter.value.value)
      ? String(filter.value.value)
      : null;

  atLeastLimit.value =
    filter.value.type === SimpleBooleanFilterType.AT_LEAST &&
    Number(filter.value.value)
      ? String(filter.value.value)
      : null;

  setMediaTypes();
  setSocialPlatforms();
  originalFilter.value = filter.value;
}

function cancel() {
  filter.value = originalFilter.value;
  reset();
  closeDropdown();
  emit("cancel");
}

onMounted(() => {
  reset();
});

watch(dropdownOpen, (value) => {
  if (!value) {
    activeOption.value = null;
  } else {
    reset();
    activeOption.value = filter.value.type || null;
  }
});

defineExpose({
  close: closeDropdown,
});
</script>

<style scoped>
.selector-button {
  --c-button-height: 20px;
  --c-button-font-size: 10px;
}
</style>
